import React, { useContext, useState } from "react";
import {
  Tooltip,
  IconButton,
  Menu,
  Box,
  Typography,
  TextField,
} from "@mui/material";
import AddCommentIcon from "@mui/icons-material/AddComment";
import CustomButton from "../CustomButton/CustomButton";
import { handleAddCommentOnTrack } from "../../services/commentsService";
import useGetValidAccessToken from "../../hooks/useGetValidAccessToken";
import {
  AuthContext,
  AuthContextInterface,
  UserDetailsContext,
  UserDetailsContextInterface,
} from "../../contexts";
import {
  AddTimeStampCommentResponse,
  AudioComment,
  Comment,
} from "../../proto/api_pb";
import { toast } from "react-toastify";
import { styles } from "./styles";

interface AddCommentModalProps {
  trackId: string;
  versionId: string;
  commentTime: number;
  refreshComments: () => void;
  pauseTrack: () => void;
  playTrack: () => void;
}

const AddCommentModal: React.FC<AddCommentModalProps> = ({
  trackId,
  versionId,
  commentTime,
  refreshComments,
  pauseTrack,
  playTrack,
}) => {
  const [newComment, setNewComment] = useState<string>("");
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const open = Boolean(anchorEl);
  const { getValidAccessToken } = useGetValidAccessToken();
  const { userId } = useContext<AuthContextInterface>(AuthContext);
  const { enterprise, role } =
    useContext<UserDetailsContextInterface>(UserDetailsContext);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    pauseTrack();
  };
  const handleClose = () => {
    setAnchorEl(null);
    playTrack();
  };

  const handleAddNewComment = async () => {
    if (!newComment.trim()) {
      return;
    }
    setLoading(true);
    const token = await getValidAccessToken();
    const audioComment = new AudioComment();
    const c = new Comment();
    c.setText(newComment);
    c.setTimestamp(commentTime);
    c.setUserId(userId);
    audioComment.setTrackId(trackId);
    audioComment.setVersionId(versionId);
    audioComment.setCommentsList([c]);
    try {
      const res = await handleAddCommentOnTrack(
        userId,
        token,
        audioComment,
        enterprise?.id ?? "",
        Number(role) ?? 0
      );
      if (res instanceof AddTimeStampCommentResponse) {
        toast.success("Comment added successfully");
        setNewComment("");
        refreshComments();
        handleClose();
        playTrack();
      }
    } catch (error) {
      console.error("Error adding comment:", error);
      toast.error("Failed to add comment");
    } finally {
      setLoading(false);
    }
  };
  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };
  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    e.stopPropagation();
  };
  return (
    <>
      <Tooltip title="Add Comment">
        <IconButton onClick={handleClick}>
          <AddCommentIcon sx={{ fontSize: "30px" }} />
        </IconButton>
      </Tooltip>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        sx={{
          "& ul": {
            margin: 0,
            padding: 0,
          },
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        PaperProps={{
          sx: {
            mt: -8,
          },
        }}
      >
        <Box sx={styles.commentContainer}>
          <Box sx={{ marginBottom: "10px" }}>
            <Typography sx={{ marginBottom: "10px" }}>
              Add Comment at {formatTime(commentTime)}
            </Typography>
            <TextField
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              fullWidth
              autoFocus
              onKeyDown={onKeyDown}
            />
          </Box>
          <Box sx={styles.commentsButtonContainer}>
            <CustomButton
              text="Add comment"
              loading={loading}
              onClick={handleAddNewComment}
              customStyles={{ width: "49%" }}
            />
            <CustomButton
              text="Close"
              onClick={handleClose}
              isSkipButton={true}
              customStyles={{ width: "49%" }}
            />
          </Box>
        </Box>
      </Menu>
    </>
  );
};

export default AddCommentModal;
